import { useCallback, useEffect, useState } from 'react';
import { ResearchSheet, UserInfo, WhoIsInfo } from 'types';

import { getResearchSheet } from 'modules/portfolio-research-sheet/research-sheet-api';

import { fetchUserInfo, fetchWhoIsInfo } from './auth-api';

export function useAuth() {
  /**
   * Indicates if userInfo is fetched.
   */
  const [userLoaded, setUserLoaded] = useState(false);
  const userSheetId = localStorage.getItem('userSheetId') ?? undefined;

  /**
   * Holds AIS UK userInfo.
   */
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(undefined);
  const [userSheet, setUserSheet] = useState<ResearchSheet | undefined>(
    undefined
  );

  const refreshUser = useCallback(async () => {
    try {
      const userInfoPromise = fetchUserInfo();
      const userSheetPromise = getResearchSheet(userSheetId);

      const [newUserInfo, newUserSheet] = await Promise.all([
        userInfoPromise,
        userSheetPromise,
      ]);

      setUserInfo(newUserInfo);
      setUserSheet(newUserSheet);
    } catch (e) {
      setUserInfo(undefined);
    }

    setUserLoaded(true);
  }, [userSheetId]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const researchSheetToDisplayInOrder = userSheet?.id
    ? userSheet.ordinalNumber
    : userInfo?.currentlyUsedSheetOrdinalNumber;

  return {
    userLoaded,
    userInfo,
    setUserInfo,
    userSheet,
    setUserSheet,
    refreshUser,
    researchSheetToDisplayInOrder,
  };
}

export function useWhoIs() {
  /**
   * Indicates if whoIsInfo is fetched.
   */
  const [whoIsLoaded, setWhoIsLoaded] = useState(false);

  /**
   * Holds WhoIs info.
   */
  const [whoIsInfo, setWhoIsInfo] = useState<WhoIsInfo | undefined>(undefined);

  const refreshWhoIs = async () => {
    try {
      const whoIsResponse = await fetchWhoIsInfo();

      setWhoIsInfo(whoIsResponse);
    } catch (e) {
      setWhoIsInfo(undefined);
    }

    setWhoIsLoaded(true);
  };

  useEffect(() => {
    refreshWhoIs();
  }, []);

  return {
    whoIsInfo,
    refreshWhoIs,
    whoIsLoaded,
  };
}
